import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  BookOpen,
  Code,
  Database,
  Layers,
  Smartphone,
  BarChart3,
  Palette,
  Globe,
  Megaphone,
  Calendar,
  ListTodo,
  Paintbrush,
  Monitor,
  Brain,
  Lock,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import AuthModal from '../components/AuthModal';
import { getActiveTracks, getTrackByName } from '../data/tasks';

const TracksPage = () => {
  const navigate = useNavigate();

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedTrackForAuth, setSelectedTrackForAuth] = useState(null);

  const tracks = getActiveTracks().map(track => track.name);

  const getTrackIcon = (trackName) => {
    const track = getTrackByName(trackName);
    if (!track) return BookOpen;
    
    switch (track.icon) {
      case 'Code':
        return Code;
      case 'Database':
        return Database;
      case 'Layers':
        return Layers;
      case 'Smartphone':
        return Smartphone;
      case 'BarChart3':
        return BarChart3;
      case 'Palette':
        return Palette;
      case 'Globe':
        return Globe;
      case 'Paintbrush':
        return Paintbrush;
      case 'Megaphone':
        return Megaphone;
      case 'Monitor':
        return Monitor;
      case 'Brain':
        return Brain;
      default:
        return BookOpen;
    }
  };

  const getTrackSlug = (trackName) => {
    const track = getTrackByName(trackName);
    return track?.slug || trackName.toLowerCase().replace(/\s+/g, '-');
  };

  const handleTrackClick = (track) => {
    setSelectedTrackForAuth(track);
    setIsAuthModalOpen(true);
  };

  const handleAuthenticated = (email) => {
    // AuthModal already validated, just navigate
    setIsAuthModalOpen(false);
    const slug = getTrackSlug(selectedTrackForAuth);
    // Navigate with authenticated state
    navigate(`/track/${slug}/tasks`, { 
      state: { 
        authenticated: true, 
        email: email,
        track: selectedTrackForAuth
      } 
    });
  };

  const handleCloseAuth = () => {
    setIsAuthModalOpen(false);
    setSelectedTrackForAuth(null);
  };

  return (
    <div className="min-h-screen bg-neutral-50 font-sans flex flex-col">
      <SEO 
        title="Internship Tracks | HindukushSoft Virtual Internship Program"
        description="Explore our internship tracks: Web Development, AI/ML, and Digital Marketing. Start your career transformation today."
        url="https://internship.hindukushsoft.com/tracks"
      />
      
      <Header />
      
      <main className="flex-1 pb-8 sm:pb-12">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="py-8 px-4 sm:px-6"
          >
            {/* Hero Section */}
            <div className="text-center mb-8">
              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-neutral-900"
              >
                Choose Your Internship Track
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="text-sm sm:text-base text-neutral-500 max-w-lg mx-auto"
              >
                Transform your career with our comprehensive internship programs
              </motion.p>
            </div>

            {/* Track Selection Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {tracks.map((track, index) => {
                const IconComponent = getTrackIcon(track);
                return (
                  <motion.div
                    key={track}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.05, duration: 0.3 }}
                    className="bg-white/88 backdrop-blur-sm border border-primary/15 rounded-2xl p-5 shadow-xl shadow-primary/5 transition-all duration-300 hover:shadow-2xl hover:border-secondary/30 cursor-pointer"
                    onClick={() => handleTrackClick(track)}
                  >
                    <div className="text-center">
                      {/* Icon */}
                      <div className="mb-4">
                        <div className="w-14 h-14 mx-auto bg-primary/10 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/10 border border-primary/10">
                          <IconComponent className="w-7 h-7 text-primary" />
                        </div>
                      </div>
                      
                      {/* Title */}
                      <h3 className="text-sm font-semibold text-neutral-900 mb-3">
                        {track}
                      </h3>
                      
                      {/* Stats */}
                      <div className="flex items-center justify-center gap-3 mb-3 text-xs text-neutral-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>8 Weeks</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ListTodo className="w-3 h-3" />
                          <span>40 Tasks</span>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <div className="mb-3">
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-600 border border-red-200">
                          <CheckCircle className="w-3 h-3" />
                          Internship Closed
                        </span>
                      </div>

                      {/* Action Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTrackClick(track);
                        }}
                        className="w-full bg-neutral-800 hover:bg-neutral-900 text-white py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-neutral-900/20"
                      >
                        <Lock className="w-4 h-4" />
                        Batch IV Closed
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </main>
      
      <Footer />

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleCloseAuth}
        onAuthenticated={handleAuthenticated}
        trackName={selectedTrackForAuth}
      />
    </div>
  );
};

export default TracksPage;
